import React, { useState, useEffect } from 'react'
import ColorUtils from '../utils/colors'
import { Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, Box, Typography, Stack, Divider, Paper, Fade } from '@mui/material';
import FormInput from '../components/FormInput'
import ContentItem from '../components/ContentItem'
import localization from '../utils/localization'
import ContentUtils from '../utils/content'
import ContentIcon from '../components/ContentIcon'
import TextLine from '../components/TextLine'
import moment from 'moment'
import ModalTotp from '../components/ModalTotp'
import ModalWallet from '../components/ModalWallet'
import ModalTag from '../components/ModalTag'
import ModalAlert from '../components/ModalAlert'
import UserUtils from '../utils/user'
import ServerUtils from '../utils/server'
import toast from 'react-hot-toast'
import AesUtils from '../utils/aes'
import ModalCreateMenu from '../components/ModalCreateMenu'
import ModalContent from '../components/ModalContent'
import content from '../utils/content'
import { pinyin } from 'pinyin-pro'
import ToolsUtils from '../utils/tools'

const DetailRow = ({ label, value, icon, color, isPassword }) => {
  const [hover, setHover] = useState(false);

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        p: 2.5,
        borderRadius: '20px',
        border: '1px solid rgba(0,0,0,0.04)',
        backgroundColor: '#fff',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
          borderColor: color,
        }
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: `${color}10`,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={`iconfont ${icon}`} style={{ fontSize: '1.3rem' }}></i>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#94A3B8',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <TextLine
              text={value}
              style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#1E293B',
                wordBreak: 'break-all',
                fontFamily: isPassword ? "'JetBrains Mono', monospace" : 'inherit'
              }}
            />
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

const SortEnumList = [{
  type: 'sortByTimeDesc',
  text: localization.strings.sortByTimeDesc,
  icon: 'icon-sort-amount-down1',
}, {
  type: 'sortByTimeAsc',
  text: localization.strings.sortByTimeAsc,
  icon: 'icon-sort-amount-up',
}, {
  type: 'sortByAlphaDesc',
  text: localization.strings.sortByAlphaDesc,
  icon: 'icon-sort-alpha-down-alt',
}, {
  type: 'sortByAlphaAsc',
  text: localization.strings.sortByAlphaAsc,
  icon: 'icon-sort-alpha-up-alt',
}]

function MainContent() {
  const [contentList, setContentList] = useState([])
  const [groupKeyword, setGroupKeyword] = useState('')
  const [groupList, setGroupList] = useState([])
  const [renderGroupList, setRenderGroupList] = useState([])
  const [groupMap, setGroupMap] = useState({})
  const [selectedGroup, setSelectedGroup] = useState('')
  const [contentKeyword, setContentKeyword] = useState('')
  const [contentItemList, setContentItemList] = useState([])
  const [renderItemList, setRenderItemList] = useState([])
  const [selectedItem, setSelectedItem] = useState({})
  const [totpModalOpen, setTotpModalOpen] = useState(false)
  const [walletModalOpen, setWalletModalOpen] = useState(false)
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false)
  const [editTagModalOpen, setEditTagModalOpen] = useState(false)
  const [alertModalOpen, setAlertModalOpen] = useState(false)
  const [alertModalContent, setAlertModalContent] = useState('')
  const [alertModalHint, setAlertModalHint] = useState('')
  const [alertModalMode, setAlertModalMode] = useState('')
  const [createMenuModalOpen, setCreateMenuModalOpen] = useState(false)
  const [contentModalOpen, setContentModalOpen] = useState(false)
  const [contentModalType, setContentModalType] = useState(0)
  const [contentModalTitle, setContentModalTitle] = useState('')
  const [contentModalItem, setContentModalItem] = useState({})
  const [masterPassOpen, setMasterPassOpen] = useState(false)
  const [masterPassword, setMasterPassword] = useState('')
  const [masterPasswordErr, setMasterPasswordErr] = useState('')

  // sort
  const [anchorEl, setAnchorEl] = useState(null);
  const sortMenuOpen = Boolean(anchorEl);
  const [sortType, setSortType] = useState('sortByTimeDesc')
  const [sortIcon, setSortIcon] = useState('icon-sort-amount-down1')

  useEffect(() => {
    let tmpGroupList = ToolsUtils.sortTagList([...groupList], sortType)
    setRenderGroupList(tmpGroupList.filter((item) => !groupKeyword || item.text.toLowerCase().includes(groupKeyword.toLowerCase())))
  }, [groupKeyword, groupList, sortType])

  useEffect(() => {
    let tmpItemList = ToolsUtils.sortContentList([...contentItemList], sortType)
    setRenderItemList(tmpItemList.filter((item) => !contentKeyword || item.title.toLowerCase().includes(contentKeyword.toLowerCase())))
  }, [contentItemList, contentKeyword, sortType])

  function watchSelectedGroup() {
    let tmpItemList = []
    if (selectedGroup in groupMap) {
      tmpItemList = groupMap[selectedGroup]
    }
    setContentItemList(tmpItemList)
    for (let i in tmpItemList) {
      if (tmpItemList[i].id === selectedItem.id) {
        setSelectedItem(tmpItemList[i])
        break
      }
    }
  }

  useEffect(watchSelectedGroup, [selectedGroup, groupMap])

  useEffect(() => {
    ContentUtils.setContentListener(async (rawContentList) => {
      var tmpGroupList = []
      var tmpGroupMap = {}
      for (var i = 0; i < rawContentList.length; i++) {
        if (rawContentList[i].type === ContentUtils.getBlockChainType()) {
          rawContentList[i].detail = JSON.parse(rawContentList[i].content)
        }
        var contentItem = rawContentList[i]
        if (typeof contentItem.tags === 'object') {
          for (var j = 0; j < contentItem.tags.length; j++) {
            var tagItem = contentItem.tags[j]
            if (tagItem in tmpGroupMap) {
              tmpGroupMap[tagItem].push(contentItem)
            } else {
              tmpGroupList.push({
                id: tagItem,
                type: 0,
                text: tagItem,
              })
              tmpGroupMap[tagItem] = [contentItem]
            }
          }
        }
      }
      setContentList(rawContentList)
      setGroupList(tmpGroupList)
      tmpGroupMap[localization.strings.allPassword] = rawContentList.filter((item) => item.type === ContentUtils.getPasswordType())
      tmpGroupMap[localization.strings.allText] = rawContentList.filter((item) => item.type === ContentUtils.getTextType())
      tmpGroupMap[localization.strings.allTotp] = rawContentList.filter((item) => item.type === ContentUtils.getTotpType())
      tmpGroupMap[localization.strings.allDigitalWallet] = rawContentList.filter((item) => item.type === ContentUtils.getBlockChainType())
      setGroupMap(tmpGroupMap)
    })
  }, [])

  function alertModalCallback() {
    let userInfo = UserUtils.getUserInfo()
    switch (alertModalMode) {
      case 'delete_tag':
        let needUpdateContents = []
        for (let i in contentItemList) {
          let item = contentItemList[i]
          if (item.tags === undefined) {
            item.tags = []
          }
          if (item.tags.includes(selectedGroup)) {
            let copyItem = JSON.parse(JSON.stringify(item))
            copyItem.tags = copyItem.tags.filter((tagItem) => tagItem !== selectedGroup)
            needUpdateContents.push(copyItem)
          }
        }
        let lastModifyTime = new Date().getTime() / 1000
        ServerUtils.updateContent({
          version: userInfo.version,
          contents: needUpdateContents.map(function (contentItem) {
            return {
              content_id: contentItem.id,
              encrypted_data: AesUtils.encryptData(userInfo.account, UserUtils.getMasterPass(), JSON.stringify({
                title: contentItem.title,
                content: contentItem.content,
                type: contentItem.type,
                color: contentItem.color,
                account: contentItem.account,
                extra: contentItem.extra,
                tags: contentItem.tags,
              })),
            }
          }),
        }).then(async (resp) => {
          if (!resp.ok) {
            try {
              let respJson = await resp.json()
              if (respJson.errno !== 0) {
                toast.error(ServerUtils.handleError(respJson))
                return
              }
            } catch (e) {

            }
            toast.error(localization.strings.networkErr)
            return
          }
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
          ContentUtils.updateContents(needUpdateContents.map(function (contentItem) {
            return {
              id: contentItem.id,
              version: userInfo.version,
              title: contentItem.title,
              content: contentItem.content,
              type: contentItem.type,
              color: contentItem.color,
              account: contentItem.account,
              last_modify_time: lastModifyTime,
              extra: contentItem.extra,
              tags: contentItem.tags,
            }
          }))
          toast.success(localization.strings.successDelete)
          setSelectedGroup('')
          setSelectedItem({})
          setAlertModalOpen(false)
        })
        return
      case 'delete_content':
        let id = selectedItem.id
        ServerUtils.deleteContent(id, userInfo.version).then(async (resp) => {
          if (!resp.ok) {
            try {
              let respJson = await resp.json()
              if (respJson.errno !== 0) {
                toast.error(ServerUtils.handleError(respJson))
                return
              }
            } catch (e) {

            }
            toast.error(localization.strings.networkErr)
            return
          }
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
          ContentUtils.deleteContent(id)
          setSelectedItem({})
          setAlertModalOpen(false)
          toast.success(localization.strings.successDelete)
        })
        return
    }
  }

  function deleteTag() {
    setAlertModalContent(`${localization.strings.deleteTag} "${selectedGroup}"`)
    setAlertModalHint(localization.strings.deleteTagHint)
    setAlertModalMode('delete_tag')
    setAlertModalOpen(true)
  }

  function deleteContent() {
    setAlertModalContent(`${localization.strings.deleteContent} "${selectedItem.title}"`)
    setAlertModalHint('')
    setAlertModalMode('delete_content')
    setAlertModalOpen(true)
  }

  function checkMasterPass() {
    let userInfo = UserUtils.getUserInfo()
    if (masterPassword === '') {
      setMasterPasswordErr(localization.strings.required)
      return
    }
    var decryptedAccount = AesUtils.decryptData(userInfo.account, masterPassword, userInfo.signed_account)
    if (decryptedAccount !== userInfo.account) {
      setMasterPasswordErr(localization.strings.errorMasterPassword)
      return
    }
    setMasterPassOpen(false)
    setWalletModalOpen(true)
  }

  return (
    <div style={{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
    }}>
      {/* group sidebar */}
      <div style={{
        height: '100%',
        width: '20rem',
        display: 'flex',
        flexDirection: 'column',
        borderRight: `1px solid ${ColorUtils.divider}`,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0.8rem 1rem',
          gap: '0.8rem',
        }}>
          <Box
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              width: '2.6rem',
              height: '2.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              backgroundColor: sortMenuOpen ? '#6366F1' : 'rgba(99, 102, 241, 0.1)',
              color: sortMenuOpen ? '#fff' : '#6366F1',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: sortMenuOpen ? '#6366F1' : 'rgba(99, 102, 241, 0.15)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            <i className={`iconfont ${sortIcon}`} style={{ fontSize: '1.2rem' }}></i>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={sortMenuOpen}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                borderRadius: '18px',
                minWidth: 200,
                p: 1,
                filter: 'drop-shadow(0px 10px 25px rgba(0,0,0,0.1))',
                border: '1px solid rgba(0,0,0,0.05)',
              },
            }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          >
            {SortEnumList.map((item) => (
              <MenuItem
                key={item.type}
                onClick={() => {
                  setSortType(item.type)
                  setSortIcon(item.icon)
                  setAnchorEl(null);
                }}
                sx={{
                  borderRadius: '10px',
                  py: 1.2,
                  mb: 0.5,
                  backgroundColor: sortType === item.type ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                  color: sortType === item.type ? '#6366F1' : '#1E293B',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.05)',
                  }
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    backgroundColor: sortType === item.type ? '#6366F1' : 'rgba(0,0,0,0.04)',
                    color: sortType === item.type ? '#fff' : '#64748B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <i className={`iconfont ${item.icon}`} style={{ fontSize: '1rem' }}></i>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: sortType === item.type ? 700 : 600 }}>
                  {item.text}
                </Typography>
              </MenuItem>
            ))}
          </Menu>

          <div style={{ flex: 1 }}>
            <FormInput start_icon="icon-search1" placeholder={localization.strings.keywordHint} size="small" onChange={(event) => {
              setGroupKeyword(event.target.value)
            }} />
          </div>

          <Box
            onClick={() => setCreateTagModalOpen(true)}
            sx={{
              width: '2.6rem',
              height: '2.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
              }
            }}
          >
            <i className="iconfont icon-add" style={{ fontSize: '1.2rem' }}></i>
          </Box>
        </div>
        <div style={{
          flex: 1,
          overflowY: 'scroll',
          paddingTop: '10px',
        }}>
          {
            (!groupKeyword || localization.strings.allPassword.toLowerCase().includes(groupKeyword.toLowerCase())) && (
              <div style={{
                padding: '0rem 0.5rem 0.5rem',
              }}>
                <ContentItem icon="icon-key1" contentType={ContentUtils.getPasswordType()} text={localization.strings.allPassword}
                  active={selectedGroup === localization.strings.allPassword} onClick={() => {
                    setSelectedGroup(localization.strings.allPassword)
                  }} />
              </div>
            )
          }
          {
            (!groupKeyword || localization.strings.allText.toLowerCase().includes(groupKeyword.toLowerCase())) && (
              <div style={{
                padding: '0rem 0.5rem 0.5rem',
              }}>
                <ContentItem icon="icon-file-text" contentType={ContentUtils.getTextType()} text={localization.strings.allText} active={selectedGroup === localization.strings.allText} onClick={() => {
                  setSelectedGroup(localization.strings.allText)
                }} />
              </div>
            )
          }
          {
            (!groupKeyword || localization.strings.allTotp.toLowerCase().includes(groupKeyword.toLowerCase())) && (
              <div style={{
                padding: '0rem 0.5rem 0.5rem',
              }}>
                <ContentItem icon="icon-stopwatch" contentType={ContentUtils.getTotpType()} text={localization.strings.allTotp} active={selectedGroup === localization.strings.allTotp} onClick={() => {
                  setSelectedGroup(localization.strings.allTotp)
                }} />
              </div>
            )
          }
          {
            (!groupKeyword || localization.strings.allDigitalWallet.toLowerCase().includes(groupKeyword.toLowerCase())) && (
              <div style={{
                padding: '0rem 0.5rem 0.5rem',
              }}>
                <ContentItem icon="icon-bitcoin" contentType={ContentUtils.getBlockChainType()} text={localization.strings.allDigitalWallet} active={selectedGroup === localization.strings.allDigitalWallet} onClick={() => {
                  setSelectedGroup(localization.strings.allDigitalWallet)
                }} />
              </div>
            )
          }
          {
            renderGroupList.map((item) => {
              return (
                <div key={item.text} style={{
                  padding: '0rem 0.5rem 0.5rem',
                }}>
                  <ContentItem icon="icon-tag1" contentType={ContentUtils.getTagType()} text={item.text}
                    active={selectedGroup === item.text} onClick={() => {
                      setSelectedGroup(item.text)
                    }} />
                </div>
              )
            })
          }
        </div>
      </div>

      {/* items sidebar */}
      <div style={{
        height: '100%',
        width: [localization.strings.allPassword, localization.strings.allText, localization.strings.allTotp,
        localization.strings.allDigitalWallet].includes(selectedGroup) ? '20rem' : '23rem',
        borderRight: `1px solid ${ColorUtils.divider}`,
      }}>
        {
          selectedGroup && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'hidden',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0.8rem 1rem',
                gap: '0.1rem',
              }}>
                {
                  ![localization.strings.allPassword, localization.strings.allText, localization.strings.allTotp,
                  localization.strings.allDigitalWallet].includes(selectedGroup) && (
                    <div style={{
                      backgroundColor: ColorUtils.lightRed,
                      marginRight: '0.5rem',
                      color: ColorUtils.red,
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                    }} onClick={deleteTag}>
                      <i className="iconfont icon-delete" style={{
                        fontSize: '1.3rem',
                      }}></i>
                    </div>
                  )
                }
                {
                  ![localization.strings.allPassword, localization.strings.allText, localization.strings.allTotp,
                  localization.strings.allDigitalWallet].includes(selectedGroup) && (
                    <div style={{
                      backgroundColor: ColorUtils.lightBlue,
                      marginRight: '0.5rem',
                      color: ColorUtils.blue,
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                    }} onClick={() => {
                      setEditTagModalOpen(true)
                    }}>
                      <i className="iconfont icon-pen01" style={{
                        fontSize: '1.3rem',
                      }}></i>
                    </div>
                  )
                }
                <div style={{
                  flex: 1,
                }}>
                  <FormInput start_icon="icon-search1" placeholder={localization.strings.keywordHint} size="small" onChange={(event) => {
                    setContentKeyword(event.target.value)
                  }} />
                </div>
                <div style={{
                  marginLeft: '0.5rem',
                  width: '2.6rem',
                  height: '2.6rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10B981',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
                  }
                }} onClick={() => {
                  setCreateMenuModalOpen(true)
                }}>
                  <i className="iconfont icon-add" style={{ fontSize: '1.2rem' }}></i>
                </div>
              </div>
              <div style={{
                paddingTop: '10px',
                flex: 1,
                overflowY: 'scroll',
              }}>
                {
                  renderItemList.map((item) => {
                    return (
                      <div key={item.id} style={{
                        padding: '0rem 0.5rem 0.5rem',
                      }}>
                        <ContentItem contentType={item.type} text={item.title} active={item.id === selectedItem.id} onClick={() => {
                          if (item.type === ContentUtils.getBlockChainType()) {
                            item.detail = JSON.parse(item.content)
                          }
                          setSelectedItem(item)
                        }} />
                      </div>
                    )
                  })
                }
              </div>
            </div>
          )
        }
      </div>

      {/* content */}
      {selectedItem.id && (
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            backgroundColor: '#F8FAFC',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: { xs: 2, md: 6 },
          }}
        >
          <Fade in={!!selectedItem.id} timeout={600} key={selectedItem.id}>
            <Box sx={{ width: '100%', maxWidth: '800px' }}>
              {/* Detail Header */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '28px',
                  border: '1px solid rgba(0,0,0,0.05)',
                  backgroundColor: '#fff',
                  mb: 3,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Header Actions */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ position: 'absolute', top: 24, right: 24 }}
                >
                  <Box
                    onClick={() => {
                      setContentModalType(selectedItem.type)
                      switch (selectedItem.type) {
                        case ContentUtils.getPasswordType():
                          setContentModalTitle(localization.strings.modifyPassword)
                          break
                        case ContentUtils.getTextType():
                          setContentModalTitle(localization.strings.modifyText)
                          break
                        case ContentUtils.getTotpType():
                          setContentModalTitle(localization.strings.modifyTotp)
                          break
                        case ContentUtils.getBlockChainType():
                          setContentModalTitle(localization.strings.modifyBlockchain)
                          break
                        default:
                          break
                      }
                      setContentModalItem(selectedItem)
                      setContentModalOpen(true)
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(30, 136, 229, 0.1)',
                      color: '#1E88E5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.2)', transform: 'translateY(-2px)' }
                    }}
                  >
                    <i className="iconfont icon-pen01" style={{ fontSize: '1.2rem' }}></i>
                  </Box>
                  <Box
                    onClick={deleteContent}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      color: '#EF4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)', transform: 'translateY(-2px)' }
                    }}
                  >
                    <i className="iconfont icon-delete" style={{ fontSize: '1.2rem' }}></i>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={3} alignItems="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '24px',
                      backgroundColor: 'rgba(30, 136, 229, 0.08)',
                      color: '#1E88E5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ContentIcon contentType={selectedItem.type} size="3rem" fontSize="2.2rem" />
                  </Box>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: '#1E293B',
                        fontFamily: "'Poppins', sans-serif",
                        mb: 0.5,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selectedItem.title}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {selectedItem.tags && selectedItem.tags.map((tag, i) => (
                        <Box
                          key={i}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            color: '#64748B',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                          }}
                        >
                          #{tag}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>

              {/* Detail Content */}
              <Stack spacing={2}>
                {/* Specific Fields */}
                {selectedItem.type === ContentUtils.getPasswordType() && (
                  <DetailRow label={localization.strings.account} value={selectedItem.account} icon="icon-person" color="#1E88E5" />
                )}

                {selectedItem.type === ContentUtils.getBlockChainType() && (
                  <>
                    <DetailRow label={localization.strings.coinName} value={selectedItem.detail.coinName} icon="icon-bitcoin" color="#F59E0B" />
                    <DetailRow label={localization.strings.address} value={selectedItem.detail.address} icon="icon-link" color="#10B981" />
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '20px',
                        border: '1px solid rgba(0,0,0,0.04)',
                        backgroundColor: '#fff',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(99, 102, 241, 0.1)', color: '#6366F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="iconfont icon-lock" style={{ fontSize: '1.2rem' }}></i>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748B' }}>{localization.strings.walletDetail}</Typography>
                        </Stack>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setMasterPassOpen(true)}
                          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, backgroundColor: '#6366F1' }}
                        >
                          {localization.strings.showWalletDetail}
                        </Button>
                      </Stack>
                    </Paper>
                  </>
                )}

                {ContentUtils.getContentKey(selectedItem.type) && (
                  <DetailRow
                    label={ContentUtils.getContentKey(selectedItem.type)}
                    value={selectedItem.content}
                    icon="icon-lock"
                    color="#6366F1"
                    isPassword={selectedItem.type === ContentUtils.getPasswordType()}
                  />
                )}

                {selectedItem.type === ContentUtils.getTotpType() && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: '20px',
                      border: '1px solid rgba(0,0,0,0.04)',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="iconfont icon-stopwatch" style={{ fontSize: '1.2rem' }}></i>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748B' }}>{localization.strings.googleAuthCode}</Typography>
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setTotpModalOpen(true)}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, backgroundColor: '#10B981' }}
                      >
                        {localization.strings.showGoogleAuthCode}
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Extra Fields */}
                {typeof selectedItem.extra === 'object' && Object.keys(selectedItem.extra).map((key) => (
                  <DetailRow key={key} label={key} value={selectedItem.extra[key]} icon="icon-add" color="#94A3B8" />
                ))}

                {/* Metadata */}
                <Box sx={{ pt: 2, px: 1 }}>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className="iconfont icon-time" style={{ fontSize: '0.9rem' }}></i>
                    {localization.strings.modifyTime}: {moment.unix(selectedItem.last_modify_time).format('YYYY-MM-DD HH:mm:ss')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Fade>
        </Box>
      )}

      {
        selectedItem.type === ContentUtils.getTotpType() && (
          <ModalTotp open={totpModalOpen} secret={selectedItem.content} onClose={() => {
            setTotpModalOpen(false)
          }} />
        )
      }
      {
        selectedItem.type === ContentUtils.getBlockChainType() && (
          <ModalWallet open={walletModalOpen} contentInfo={selectedItem} onClose={() => {
            setWalletModalOpen(false)
          }} />
        )
      }
      <ModalTag title={localization.strings.createTag} open={createTagModalOpen} onClose={() => {
        setCreateTagModalOpen(false)
      }} onTagName={(tagName) => {
        setSelectedGroup(tagName)
      }} tagName={''} contentList={contentList} groupMap={groupMap} sortType={sortType} />
      <ModalTag title={localization.strings.modifyTag} open={editTagModalOpen} onClose={() => {
        setEditTagModalOpen(false)
      }} onTagName={(tagName) => {
        setSelectedGroup(tagName)
      }} tagName={selectedGroup} contentItemList={contentItemList} contentList={contentList} groupMap={groupMap} sortType={sortType} />
      <ModalAlert title={localization.strings.alertDialog} content={alertModalContent} hint={alertModalHint} open={alertModalOpen} onCancel={() => {
        setAlertModalOpen(false)
      }} onConfirm={alertModalCallback} />
      <ModalCreateMenu open={createMenuModalOpen} onClose={() => {
        setCreateMenuModalOpen(false)
      }} onClick={(contentType) => {
        setCreateMenuModalOpen(false)
        setContentModalType(contentType)
        switch (contentType) {
          case ContentUtils.getPasswordType():
            setContentModalTitle(localization.strings.createPassword)
            break
          case ContentUtils.getTextType():
            setContentModalTitle(localization.strings.createText)
            break
          case ContentUtils.getTotpType():
            setContentModalTitle(localization.strings.createTotp)
            break
          case ContentUtils.getBlockChainType():
            setContentModalTitle(localization.strings.createBlockchain)
            break
        }
        setContentModalItem({})
        setContentModalOpen(true)
      }} />
      <ModalContent open={contentModalOpen} title={contentModalTitle} contentType={contentModalType} onClose={() => {
        setContentModalOpen(false)
      }} tag={[localization.strings.allPassword, localization.strings.allText, localization.strings.allTotp,
      localization.strings.allDigitalWallet].includes(selectedGroup) ? '' : selectedGroup}
        onCreate={(contentInfo) => {
          setSelectedItem(contentInfo)
          if ([localization.strings.allPassword, localization.strings.allText, localization.strings.allTotp,
          localization.strings.allDigitalWallet].includes(selectedGroup)) {
            switch (contentInfo.type) {
              case ContentUtils.getPasswordType():
                setSelectedGroup(localization.strings.allPassword)
                break
              case ContentUtils.getTextType():
                setSelectedGroup(localization.strings.allText)
                break
              case ContentUtils.getTotpType():
                setSelectedGroup(localization.strings.allTotp)
                break
              case ContentUtils.getBlockChainType():
                setSelectedGroup(localization.strings.allDigitalWallet)
                break
            }
          }
        }} contentInfo={contentModalItem} />

      { /* WalletDetail Master Password Dialog */}
      <Dialog
        open={masterPassOpen}
        onClose={() => setMasterPassOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            p: 1,
            maxWidth: '400px',
            width: '100%',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 4,
          pb: 1
        }}>
          {localization.strings.masterPassword}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          <Fade in={masterPassOpen} timeout={500}>
            <Box>
              <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center', mb: 4 }}>
                Please enter your Master Password to decrypt and view your secure wallet details.
              </Typography>

              <Stack spacing={3}>
                <form autoComplete="on" action="javascript:void(0);">
                  <FormInput
                    type="password"
                    start_icon="icon-lock"
                    label={localization.strings.masterPassword}
                    placeholder={localization.strings.inputMasterPasswordHint}
                    error={masterPasswordErr}
                    onChange={(event) => {
                      setMasterPassword(event.target.value)
                      setMasterPasswordErr('')
                    }}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        checkMasterPass()
                        return
                      }
                    }}
                  />
                </form>

                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setMasterPassOpen(false)}
                    sx={{
                      borderRadius: '14px',
                      py: 1.5,
                      fontWeight: 700,
                      color: '#64748B',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
                    }}
                  >
                    {localization.strings.cancel}
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={checkMasterPass}
                    sx={{
                      borderRadius: '14px',
                      py: 1.5,
                      fontWeight: 700,
                      backgroundColor: '#1E88E5',
                      boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#1565C0' }
                    }}
                  >
                    {localization.strings.confirm}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Fade>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MainContent;